import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n/I18nContext.jsx';
import { LANGUAGE_NAMES } from '../i18n/languages.js';
import { useLocalizedPath } from '../hooks/useLocalizedPath.js';

export default function MascotMenu({ open, onClose, authenticated }) {
  const { lang, setLanguage, t } = useTranslation();
  const lp = useLocalizedPath();
  const menuRef = useRef(null);
  const [langOpen, setLangOpen] = useState(false);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Focus first item on open
  useEffect(() => {
    if (open && menuRef.current) {
      const first = menuRef.current.querySelector('[role="menuitem"]');
      if (first) first.focus();
    }
  }, [open]);

  // Reset language submenu when menu closes
  useEffect(() => {
    if (!open) setLangOpen(false);
  }, [open]);

  if (!open) return null;

  function handleLangSelect(code) {
    setLanguage(code);
    setLangOpen(false);
    onClose();
  }

  const currentLangName = LANGUAGE_NAMES.find(l => l.code === lang)?.native ?? lang;

  const publicLinks = [
    { to: lp('/'), label: t('nav.home') },
    { to: lp('/pricing'), label: t('nav.pricing') },
    { to: lp('/contact'), label: t('nav.contact') },
  ];

  const appLinks = [
    { to: lp('/dashboard'), label: t('nav.dashboard') },
    { to: lp('/connector'), label: t('nav.connector') },
  ];

  const navLinks = authenticated ? appLinks : publicLinks;

  return (
    <>
      <div className="mascot-menu-backdrop" onClick={onClose} />
      <div
        ref={menuRef}
        className="mascot-menu"
        role="menu"
        aria-label={t('nav.menuAriaLabel')}
      >
        {/* Nav links */}
        <div className="mascot-menu-section">
          {navLinks.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className="mascot-menu-item"
              role="menuitem"
              onClick={onClose}
            >
              {label}
            </Link>
          ))}
          {!authenticated && (
            <Link
              to={lp('/login')}
              className="mascot-menu-item mascot-menu-cta"
              role="menuitem"
              onClick={onClose}
            >
              {t('nav.getStarted')}
            </Link>
          )}
        </div>

        <div className="mascot-menu-divider" />

        {/* Language toggle — shows current language, expands to list */}
        <div className="mascot-menu-section">
          <button
            className="mascot-menu-item mascot-menu-lang-toggle"
            role="menuitem"
            aria-expanded={langOpen}
            onClick={() => setLangOpen(prev => !prev)}
          >
            <span>
              {t('nav.languageSection')}
              <span style={{ marginLeft: '0.4rem', color: 'var(--color-muted)', fontSize: '0.85rem' }}>
                {currentLangName}
              </span>
            </span>
            <span className="mascot-menu-lang-chevron" aria-hidden="true"
              style={{ transform: langOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>
              ›
            </span>
          </button>

          {langOpen && (
            <div className="mascot-menu-lang-list">
              {LANGUAGE_NAMES.map(({ code, native }) => (
                <button
                  key={code}
                  className={`mascot-menu-item mascot-menu-lang${code === lang ? ' active' : ''}`}
                  role="menuitem"
                  aria-checked={code === lang}
                  onClick={() => handleLangSelect(code)}
                >
                  <span>{native}</span>
                  {code === lang && <span className="mascot-menu-lang-check" aria-hidden="true">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mascot-menu-divider" />

        {/* Footer links */}
        <div className="mascot-menu-section mascot-menu-footer">
          <Link to={lp('/privacy')} className="mascot-menu-item" role="menuitem" onClick={onClose}>
            {t('footer.colCompanyPrivacy')}
          </Link>
          <Link to={lp('/terms')} className="mascot-menu-item" role="menuitem" onClick={onClose}>
            {t('footer.colCompanyTerms')}
          </Link>
        </div>
      </div>
    </>
  );
}
